import { Controller } from '@hotwired/stimulus';

import { fold, foldedRange } from '../utils/fold_text.js';

// The trigger dot's invariant classes, IDENTICAL to what the component renders
// server-side (Form::SearchableSelectComponent). Only the colour class varies, and it
// always comes from the server (`data-dot-class` on the chosen <li>): this controller
// never builds markup nor derives a colour.
const DOT_BASE_CLASSES = 'inline-block h-2 w-2 shrink-0 rounded-full';

// The dot's HIDDEN state, mirroring Form::SearchableSelectComponent::DOT_HIDDEN_CLASSES.
// `inline-block` is deliberately ABSENT rather than joined with `hidden`: Tailwind v4
// emits `.hidden` BEFORE `.inline-block` (alphabetical, measured in the built CSS), so on
// one element the display utility wins and the dot would stay visible. Dropping it is
// what the server does too, so a JS hide lands on the EXACT markup of a server render.
const DOT_HIDDEN_CLASSES = 'hidden h-2 w-2 shrink-0 rounded-full';

// TREE MODE: the separator of the parents' path, twin of `Plans::Tree::PATH_SEPARATOR`
// (app/services/plans/tree.rb). Typography, not copy: nothing here is translated.
const PATH_SEPARATOR = ' › ';

// TREE MODE: the marker of the <mark> this controller inserts, so a reset only ever
// flattens a label it highlighted itself (never server-rendered children).
const HIGHLIGHT_ATTRIBUTE = 'data-searchable-select-highlight';

// TREE MODE: the marker of an ancestor kept ONLY for context during a search (it does not match
// itself). The template greys it and hides its intervenant through `data-[tree-context]:` /
// `group-data-[tree-context]:` variants: this controller toggles the attribute, never a class.
const CONTEXT_ATTRIBUTE = 'data-tree-context';

// MULTIPLE MODE: the checked labels joined in the trigger, twin of
// `Form::SearchableSelectComponent::MULTIPLE_TRIGGER_SEPARATOR`. Typography, not copy.
const MULTIPLE_TRIGGER_SEPARATOR = ', ';

// MULTIPLE MODE: the selection action an enabled row carries, IDENTICAL to the component's
// `data-action`. `setDisabled` removes it from a row it locks and puts it back on a row it unlocks.
const SELECT_ACTION = 'click->searchable-select#select';

// MULTIPLE MODE: the two text holders of a row's lock, rendered EMPTY on every flat multiple row
// (Form::SearchableSelectComponent, étape 3.2 D2) and written by `setDisabled` with textContent.
const REASON_TEXT_ROLE = 'disabled-reason-text';
const EXPLANATION_ROLE = 'disabled-explanation';

/**
 * Searchable Select Controller (Form::SearchableSelectComponent)
 *
 * Generic combobox: a design-system replacement for a native <select> with
 * filter-as-you-type. SINGLE by default: the chosen value is written to a HIDDEN input
 * (`hiddenInput`), so the params contract is that of a plain <select>. MULTIPLE on
 * opt-in (see MULTIPLE MODE below): `…[]` inputs, as a native <select multiple>.
 *
 * Distilled from contractor_combobox_controller.js MINUS the partner-group /
 * separator bookkeeping — a flat listbox of options + a "no results" row.
 *
 * Targets:
 * - trigger: the disclosure button that opens the panel.
 * - selected: the trigger's visible text span (updated on select).
 * - dropdown: the panel (search input + listbox); toggled via `hidden`.
 * - input: the role=combobox search field — the focused element, so it hosts
 *   aria-activedescendant + aria-expanded (per the ARIA combobox pattern).
 * - hiddenInput: the value carrier posted with the form.
 * - listbox: the <ul role=listbox> the combobox controls (via aria-controls).
 * - option: each <li role=option>.
 * - optionLabel: the option's text span (source of the trigger label on select).
 * - checkmark: per-option selected tick.
 * - noResults: the "Aucun résultat" row, shown when the filter matches nothing.
 * - dot (OPTIONAL): the trigger's colour dot, a SIBLING of `selected`.
 * - selectedPath (OPTIONAL, tree mode only): the trigger's small line ABOVE `selected`,
 *   holding the chosen lot's parents joined by « › » (D12).
 * - treeHeading (OPTIONAL, tree mode only): the <li role=presentation> group heading
 *   (« Lots du planning »), hidden while a search leaves no tree row visible.
 *
 * Classes:
 * - highlight (OPTIONAL, tree mode only): the semantic-token classes put on the <mark>
 *   wrapping the searched fragment. Absent, the browser's own <mark> style applies.
 *
 * Values:
 * - placeholder (String): the muted text shown when nothing (or the blank option)
 *   is selected.
 * - blankIsChoice (Boolean, default false): the `include_blank` row is a REAL choice
 *   (« Aucun (racine) » of « Lot parent », B03 état 3). Picking it writes ITS label in ink
 *   into the trigger, as the server renders it for an empty value, instead of the muted
 *   placeholder. A search then Escape never touches the trigger, so the label stays.
 *
 * TRIGGER TITLE (DS-8, every caller of the component, independent of `panel_width`): the
 * server renders a `title` on the `selected` span (the full text of a label the trigger may
 * cut). When it is there, every choice rewrites it with the shown text (setAttribute, never
 * innerHTML).
 *
 * TWO MODES, told apart by the presence of the search input (`searchable:` on the
 * component — the input target is simply not rendered when it is false):
 * - SEARCHABLE (default, e.g. « Concerné »): the panel holds the role=combobox field,
 *   focus moves into it on open, it hosts aria-expanded + aria-activedescendant, and
 *   typing filters the options.
 * - CLOSED LIST (`searchable: false`, e.g. the CR remark « Statut » and its handful of
 *   agency statuses): no input exists. The TRIGGER keeps focus and becomes the keyboard
 *   host — `handleTriggerKeydown` opens the panel, then routes the navigation keys to
 *   the SAME `handleKeydown`, so ArrowDown/ArrowUp/Enter/Space work from the trigger
 *   and Tab closes the panel without being cancelled. Every read of `inputTarget` is
 *   therefore guarded by `hasInputTarget`, and `search()` returns early (there is
 *   nothing to type in). The trigger carries `aria-controls` to the listbox in this
 *   mode (the component renders it), since no combobox input does.
 *   Accepted limitation: with no focused combobox to carry aria-activedescendant, the
 *   roving highlight is visual only; what is SELECTED stays announced through the
 *   options' aria-selected and the trigger's own text.
 *
 * OPTIONAL COLOUR DOT (`dot` target):
 * - Fed options that carry a `dot_class` (the remark statuses), the component renders a
 *   dot in the trigger as a SIBLING — never a child — of the `selected` span, because
 *   `select()` overwrites that span's textContent and would wipe any nested node.
 * - On select, the dot's class list is rebuilt as DOT_BASE_CLASSES + the `data-dot-class`
 *   the SERVER put on the chosen <li>. Never innerHTML, never a colour derived here
 *   (#591), and the classes stay literal so the Tailwind JIT sees them in the ERB.
 * - An option with no `data-dot-class` (the blank row, or a plain option list) hides the
 *   dot by swapping in DOT_HIDDEN_CLASSES — see that constant for why `inline-block` is
 *   DROPPED rather than `hidden` appended. Both states are byte-equal to what a server
 *   render produces, and no inline style is ever written.
 *
 * « LOT PARENT » (planning, refonte PR 2, Task 7 ; B03-13, B03-14, RV-39), opt-in on the
 * component, inert for every other caller:
 * - `search()` filters on the option's LABEL (`optionLabel`), never on the row's whole
 *   textContent: a disabled row renders its reason next to the label, and « déc » must
 *   not match « Demande de décalage en attente ».
 * - A row marked `data-pinned="true"` (the « Aucun (racine) » row, `pin_blank: true`) is
 *   never filtered out.
 * - A row marked `aria-disabled="true"` STAYS navigable (the highlight and
 *   aria-activedescendant land on it, so its reason is read out), but `select()` ignores
 *   it: Enter and a click choose nothing and dispatch no `change`.
 *
 * TREE MODE (sélecteur « Lot » de la remarque de CR ; D11, D12, #591), DEDUCED from the
 * markup (a `selectedPath` target, or an option carrying `data-parent-value`), never from
 * a value. Off, nothing below runs and the flat behaviour is untouched.
 * - `search()` keeps every ANCESTOR of a found row visible, recursively through
 *   `data-parent-value` (the parent's `data-value`), so a found sub-lot stays in context.
 *   The children of a found row stay hidden unless they match themselves (maquette 1f).
 *   The BLANK row (« Aucun lot ») always stays; a pinned row (« Toutes les entreprises ») is
 *   filtered on its label like any other (QA-RL-33). The `treeHeading` target (« Lots du
 *   planning ») is hidden while no tree row is visible, shown again by an empty search or
 *   `showAllOptions`. When nothing but the blank row is left, the flat mode's `noResults` row
 *   (« Aucun résultat ») shows under it (QA-RL-88). No chevrons: nothing is collapsible (D11).
 * - An ancestor kept ONLY for context (it does not match itself) carries `data-tree-context`
 *   (QA-RL-177, planche 1f); a found row never does, and an empty search or `showAllOptions`
 *   removes it. The template styles it through `data-[…]:` variants (grey text, intervenant
 *   hidden): this controller writes no class for it.
 * - The found fragment of each matching label is wrapped in a <mark> built from DOM
 *   nodes (createElement + text nodes, never innerHTML: a label reading
 *   « <img src=x onerror=…> » must stay text, #591). Every search, and showAllOptions,
 *   first flattens the previous <mark> back to a single text node.
 * - Choosing a row writes its label into `selected`, its parents' path into
 *   `selectedPath` (empty for a root lot or the blank row) and the FULL path, lot
 *   included, into `selected`'s title (D12). The path is rebuilt from `data-parent-value`;
 *   the server renders no `data-path`.
 *
 * ONE VALUE SIGNAL: the bubbling `change` of `hiddenInput` (#applySelection; a silent write
 * never emits it). The search field's own native `change` (a typed text left by Escape, Tab or
 * a click elsewhere) is stopped at the root (`containSearchChange`), so a caller's `change->…`
 * action on an ancestor only ever hears a value.
 *
 * PUBLIC API for another controller, reached through
 * `application.getControllerForElementAndIdentifier`, never through an action:
 * - `selectValue(value)` chooses the option of that value exactly as a click does (bubbling
 *   `change` included), focus left alone.
 * - `writeValueSilently(value)` paints that option the SAME way (#paintSelection) with NO
 *   `change`, no focus move, no panel change: for a caller mirroring a state it owns, whose own
 *   `change->` action would otherwise loop (`remarks-filter`, plan review C2). '' on a list with
 *   no '' option CLEARS the selection (placeholder, no dot, no tick), as the server renders an
 *   empty value: `cr-inline-field` repaints a failed save's restored '' that way.
 *
 * MULTIPLE MODE (`multiple: true` on the component, remarque multi-entreprises, F26), read from the
 * `multiple` VALUE, never deduced. Off (every caller before it), nothing below runs.
 * - Targets: `hiddenInput` is the LEADING EMPTY input (value '', name `…[]`): it is never written, it
 *   keeps its two roles, the ONE `change` signal and the save lock. `values` holds one `valueInput`
 *   per POSTED value (checked AND enabled), in list order. `counter` (role=status) and `done`
 *   (« Terminer », `searchable-select#finish`) sit in the panel footer.
 * - `select` TOGGLES an enabled option (aria-selected + its `checkmark` wrapper's `hidden`) and keeps
 *   the panel OPEN. Ticking a `data-exclusive` row (« Toutes les entreprises ») unticks every other
 *   ENABLED row; ticking any other row unticks the exclusive one. A disabled row (`aria-disabled`, a
 *   derived intervenant shown ticked and locked) never changes and is never posted.
 * - After each toggle: the `valueInput`s are REBUILT by createElement (never innerHTML, #591), `name`
 *   copied from `hiddenInput`; the counter reads `${n} ${n <= 1 ? counterOne : counterOther}` (n =
 *   ticked rows, see COUNTER below, 0 and 1 singular as on the server); the trigger reads the checked labels (disabled
 *   included, as the server renders them) joined by ', ', or the muted placeholder, `title` included,
 *   all by textContent / setAttribute. Then ONE bubbling `change` from `hiddenInput`, whatever the
 *   number of rows an exclusive tick unticked.
 * - COUNTER (étape 3.2, D1): n = the TICKED rows, disabled (derived) ones INCLUDED, as the approved
 *   mockup reads « 3 concernés · Terminer » with two derived intervenants. What is POSTED is unchanged.
 * - Keyboard: Enter (and Space in closed-list mode) on the highlighted row toggles it, panel open. Tab
 *   does NOT close the panel, so focus reaches « Terminer »; the panel closes when focus LEAVES the
 *   component (`focusout` whose `relatedTarget` is outside the root). A `focusout` with no
 *   `relatedTarget` (a click on a non-focusable row) closes nothing: a click elsewhere is
 *   `clickOutside`'s. Escape closes as in single mode (`escapeGuard`), `finish` closes and refocuses
 *   the trigger.
 * - EXTERNAL TRIGGER (`trigger: :external` on the component, value `externalTrigger`, étape 3.2 D3):
 *   no `trigger` / `selected` target is rendered. An OUTSIDE control opens the panel by calling
 *   `open({ opener })` (`remark-lot#openLots` / `#openConcerned`, the « Ajouter » ghost buttons);
 *   without `opener`, the element focused at that moment (`document.activeElement`) is remembered.
 *   The opener carries `aria-expanded` while the panel is open, a click on it is never a click
 *   outside (the opening click bubbles to `document` AFTER `open`), and « Terminer » / Escape give
 *   focus back to it. With no search field the LISTBOX is the keyboard host (`tabindex="-1"`, its own
 *   `keydown` action, `aria-activedescendant` on it), focused on open.
 *
 * PUBLIC API IN MULTIPLE MODE (another controller, `remark-lot`, through
 * `application.getControllerForElementAndIdentifier`):
 * - `values` (getter): the POSTED values (checked AND enabled), in list order.
 * - `setValues(values, { silent = false } = {})`: makes the checked set of the ENABLED rows exactly
 *   `values` ('' entries dropped: the leading empty input's marker, never a value), with the same
 *   repaint as a toggle (inputs, counter, trigger). ALL OR NOTHING: false, nothing written, when a
 *   value names no enabled row (unknown, or a disabled row: those are the server's) or names the
 *   exclusive row together with another. Disabled rows never change. `silent: false` dispatches ONE
 *   bubbling `change`; `silent: true` none (a caller mirroring a state it owns). Focus and panel are
 *   left alone; not gated by the save lock (as the single-mode writers).
 * - `setDisabled(map, { silent = false } = {})` (étape 3.2, D2): `map` is a plain object
 *   `{ value => { reason, explanation } | null }`. A lock entry sets `aria-disabled="true"` on the row,
 *   removes its selection `data-action` and writes `reason` / `explanation` into the row's two lock
 *   nodes by textContent (never innerHTML, #591); `null` removes `aria-disabled`, puts the action back
 *   and empties both texts. The TICK is left as it is (`setValues` ticks enabled rows: tick a row
 *   BEFORE locking it, untick it AFTER unlocking it). Then the same repaint as a toggle (inputs, counter,
 *   trigger) and ONE bubbling `change` only when the POSTED values changed (`silent: true`: none).
 *   ALL OR NOTHING: false, nothing written, when a key names no row. Not gated by the save lock.
 * - `open({ opener } = {})` is public too (EXTERNAL TRIGGER above).
 * - `selectValue` / `writeValueSilently` THROW in multiple mode, and `values` / `setValues` /
 *   `setDisabled` THROW in single mode: a caller wired to the wrong mode fails loudly instead of
 *   posting a wrong value.
 *
 * SAVE LOCK: while `hiddenInput` is `disabled` (a caller saving the value, `cr-inline-field`),
 * the list neither opens (click, keyboard, `open()`) nor chooses (`select`). The
 * trigger itself is never disabled, so it keeps focus. The two public writers are not locked.
 *
 * LIST SEPARATOR: a `<li role="presentation">` between options (the « Statut » filter's rule) is
 * markup only. It carries no target and no `data-value`, so the keyboard (`optionTargets`), the
 * filter and both public writers never see it.
 *
 * Escape capture-guard contract:
 * - While the panel is open, a document-level CAPTURE-phase `keydown` listener
 *   (`escapeGuard`) eats Escape BEFORE it reaches any ancestor bubble listener —
 *   so it works no matter where focus sits (search input, padding, scrollbar, or
 *   Tab-away). The topmost open overlay owns Escape: it closes the dropdown only,
 *   never the enclosing slide-over, so an in-progress remark form is never lost.
 * - Registered in open(), removed in BOTH close() and disconnect() with the SAME
 *   `true` capture flag (capture is part of listener identity — removing without
 *   it no-ops and leaks across Turbo visits, see slide_over_controller.js:62-63).
 *   close() is idempotent, so a double removal is a safe no-op.
 * - Known marginal case (accepted, matches the planning fullscreen precedent): a
 *   command palette opened UNDER an already-open dropdown would have its first
 *   Escape eaten by this guard.
 */
export default class extends Controller {
  static targets = [
    'trigger',
    'selected',
    'dropdown',
    'input',
    'hiddenInput',
    'listbox',
    'option',
    'optionLabel',
    'checkmark',
    'noResults',
    // Optional: only rendered when the options carry a dot_class.
    'dot',
    // Optional: only rendered in tree mode (the parents' path above the chosen lot).
    'selectedPath',
    // Optional: only rendered in tree mode with a `tree_heading:` (« Lots du planning »).
    'treeHeading',
    // Multiple mode only: the container of the posted inputs, those inputs, the footer counter
    // and « Terminer ».
    'values',
    'valueInput',
    'counter',
    'done',
  ];

  static classes = ['highlight'];

  static values = {
    placeholder: String,
    blankIsChoice: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    counterOne: String,
    counterOther: String,
    // Multiple mode only (`trigger: :external`): no trigger button, an outside control opens the panel.
    externalTrigger: { type: Boolean, default: false },
  };

  connect() {
    this.focusedIndex = -1;
    this.clickOutsideHandler = this.clickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);
    // Bind ONCE — a stable reference so open()/close() add and remove the SAME
    // listener. A per-open arrow function would register unremovable duplicates.
    this.boundEscapeGuard = this.escapeGuard.bind(this);
    // On the ROOT, not on the search input: `inputTarget` is re-read on every event, so a
    // Turbo morph that swaps the field cannot strand the guard on a detached node.
    this.searchChangeGuard = this.containSearchChange.bind(this);
    this.element.addEventListener('change', this.searchChangeGuard);
    // Multiple mode: the panel closes when focus leaves the component (Tab no longer closes it).
    // Returns at once in single mode.
    this.focusOutHandler = this.handleFocusOut.bind(this);
    this.element.addEventListener('focusout', this.focusOutHandler);
  }

  disconnect() {
    document.removeEventListener('click', this.clickOutsideHandler);
    // Symmetric teardown if we disconnect while open (Turbo visit mid-open).
    document.removeEventListener('keydown', this.boundEscapeGuard, true);
    this.element.removeEventListener('change', this.searchChangeGuard);
    this.element.removeEventListener('focusout', this.focusOutHandler);
  }

  // MULTIPLE MODE: focus moved to an element OUTSIDE the component (Tab from « Terminer », Shift+Tab
  // out of the trigger…) closes the panel. No `relatedTarget` (a click on a non-focusable row, the
  // window losing focus) closes nothing: a click elsewhere is `clickOutside`'s. Nor does a focusable
  // ANCESTOR (the slide-over panel, `tabindex="-1"`): a mousedown on a row moves focus to it, and
  // closing there would swallow the click before it ticks the row (e2e, step 3.2c).
  handleFocusOut(event) {
    if (!this.#multiple || !this.isOpen) return;
    const next = event.relatedTarget;
    if (next && !this.element.contains(next) && !next.contains(this.element)) this.close();
  }

  // MULTIPLE MODE: « Terminer ». Closes the panel and gives focus back to the trigger (or, with an
  // external trigger, to the control that opened it).
  finish() {
    this.close();
    this.#returnFocus();
  }

  get #external() {
    return this.externalTriggerValue === true;
  }

  // Where focus goes back once the panel closes on purpose (« Terminer », Escape).
  #returnFocus() {
    if (this.#external) {
      this.opener?.focus?.();
    } else {
      this.triggerTarget.focus();
    }
  }

  // The element that carries `aria-expanded`: the trigger, or the outside opener (external trigger).
  #setExpanded(expanded) {
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', String(expanded));
    if (this.#external) this.opener?.setAttribute?.('aria-expanded', String(expanded));
  }

  // The element that holds `aria-activedescendant`: the search field, else (external trigger, closed
  // list) the listbox itself, else nothing (closed list with a trigger: visual highlight only).
  #activeDescendantHost() {
    if (this.hasInputTarget) return this.inputTarget;
    if (this.#external && this.hasListboxTarget) return this.listboxTarget;
    return null;
  }

  get #multiple() {
    return this.multipleValue === true;
  }

  // SEARCH `change` CONTAINMENT. The search field is an <input type="text">: the browser
  // fires a NATIVE `change` on it when a typed text is left (Escape, Tab, a click
  // elsewhere), and that event bubbles to every ancestor, where it looks like a change of
  // VALUE (`change->remark-lot#change` around the CR remark « Lot »,
  // `change->planning-phase-form#syncMarcheFromParent` around « Lot parent »). The typed
  // text is a filter, never a value: the event stops at this root. The hidden input's
  // `change`, dispatched by #applySelection, is the component's ONE value signal and keeps
  // bubbling. Bubble phase on purpose: a capture-phase ancestor listener still sees it.
  containSearchChange(event) {
    if (this.hasInputTarget && event.target === this.inputTarget) event.stopPropagation();
  }

  toggle(event) {
    event.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // LOCKED while the hidden input is `disabled`: a caller saving the value (the CR remark
  // « Statut », `cr-inline-field#setControlsDisabled`) disables ONLY the hidden input, so the
  // trigger keeps focus (C3). Opening, then choosing, would queue a second value against a
  // stale rollback snapshot. Every opening path (click, keyboard, direct call) lands here.
  get #locked() {
    return Boolean(this.hiddenInputTarget.disabled);
  }

  // `opener` (external trigger only): the outside control that asked. A Stimulus action calling
  // `open` hands it an Event, which carries no `opener`: harmless.
  open({ opener } = {}) {
    if (this.#locked) return;

    if (this.#external) this.opener = opener ?? globalThis.document?.activeElement ?? null;
    this.dropdownTarget.classList.remove('hidden');
    this.#setExpanded(true);
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute('aria-expanded', 'true');
      this.inputTarget.value = '';
      this.inputTarget.focus();
    } else {
      // CLOSED LIST mode: there is no field to focus and the <li> options are not
      // focusable, so focus STAYS on the trigger — which is then the keyboard host
      // (see handleTriggerKeydown). Focusing explicitly matters because a click on a
      // button does not focus it in Safari, and open() can also be called
      // programmatically. EXTERNAL TRIGGER: there is no trigger, the listbox is the host.
      if (this.#external) {
        this.listboxTarget.focus();
      } else {
        this.triggerTarget.focus();
      }
    }
    this.showAllOptions();
    this.focusedIndex = -1;
    // EXTERNAL TRIGGER: the panel opens under an « Ajouter » that may sit low in the drawer; bring its
    // rows and « Terminer » into view (R3 design review).
    if (this.#external) this.dropdownTarget.scrollIntoView?.({ block: 'nearest' });
    // Capture-phase so this open overlay eats Escape before any ancestor bubble
    // listener (e.g. slide_over_controller.js:98), regardless of where focus sits.
    document.addEventListener('keydown', this.boundEscapeGuard, true);
  }

  close() {
    this.dropdownTarget.classList.add('hidden');
    this.#setExpanded(false);
    this.focusedIndex = -1;
    if (this.hasInputTarget) this.inputTarget.setAttribute('aria-expanded', 'false');
    this.#activeDescendantHost()?.removeAttribute('aria-activedescendant');
    this.optionTargets.forEach((opt) => opt.classList.remove('bg-primary-50'));
    // MUST pass the same `true` capture flag — capture is part of listener
    // identity; removing without it no-ops. Idempotent: double-removal is safe.
    document.removeEventListener('keydown', this.boundEscapeGuard, true);
  }

  get isOpen() {
    return !this.dropdownTarget.classList.contains('hidden');
  }

  // Document-level CAPTURE-phase Escape guard registered while the panel is open.
  // First Escape only closes the dropdown. Stopping propagation before the target
  // phase keeps the keydown from reaching ancestor controllers (e.g. a slide-over)
  // that also close on Escape, so an in-progress form isn't silently discarded.
  // Once closed, the guard is removed, so a second Escape still reaches the
  // slide-over. Works even when focus is off the search input (padding, scrollbar,
  // Tab-away) — the whole point of moving this off the input's keydown.
  escapeGuard(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    event.stopPropagation();
    event.preventDefault();
    this.close();
    this.#returnFocus();
  }

  search() {
    // CLOSED LIST mode has no input, so nothing can be typed and there is nothing to
    // filter. Guarded rather than assumed: the action is declared next to the input in
    // the ERB, and a future caller (or a Turbo morph mid-render) must not throw here.
    if (!this.hasInputTarget) return;

    const query = fold(this.inputTarget.value).trim();
    let visibleCount = 0;

    if (this.#isTreeMode()) {
      visibleCount = this.#filterTree(query);
    } else {
      this.optionTargets.forEach((option) => {
        // The LABEL, never the row's textContent (a disabled row also carries its reason).
        const text = fold(this.#optionText(option));
        const match = query === '' || option.dataset.pinned === 'true' || text.includes(query);
        option.classList.toggle('hidden', !match);
        if (match) visibleCount += 1;
      });
    }

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle('hidden', visibleCount > 0);
    }

    // A filter invalidates the roving focus — reset it so ArrowDown starts fresh.
    this.focusedIndex = -1;
    if (this.hasInputTarget) this.inputTarget.removeAttribute('aria-activedescendant');
    this.#navigableOptions().forEach((opt) => opt.classList.remove('bg-primary-50'));
  }

  showAllOptions() {
    if (this.#isTreeMode()) {
      this.optionTargets.forEach((option) => {
        this.#clearHighlight(this.#labelElement(option));
        option.removeAttribute(CONTEXT_ATTRIBUTE);
      });
    }
    this.optionTargets.forEach((option) => option.classList.remove('hidden'));
    this.#syncTreeHeading(true);
    if (this.hasNoResultsTarget) this.noResultsTarget.classList.add('hidden');
  }

  select(event) {
    // RV-39: a disabled row is reachable (its reason is announced) but never chosen.
    if (event.currentTarget.getAttribute?.('aria-disabled') === 'true') return;
    // A panel opened BEFORE the lock (Enter on the highlighted row, a click) chooses nothing.
    if (this.#locked) return;

    if (this.#multiple) {
      this.#toggleOption(event.currentTarget);
      return;
    }

    this.#applySelection(this.#optionSelection(event.currentTarget));
  }

  // MULTIPLE MODE, public reader: the POSTED values (checked AND enabled), in list order.
  get values() {
    this.#requireMode(true, 'values');
    return this.optionTargets
      .filter((opt) => this.#isChecked(opt) && this.#isEnabled(opt))
      .map((opt) => opt.dataset.value ?? '');
  }

  // MULTIPLE MODE, public writer (see the header): the ENABLED rows checked are exactly `values`, all
  // or nothing. Returns whether it wrote.
  setValues(values, { silent = false } = {}) {
    this.#requireMode(true, 'setValues');
    const wanted = new Set(Array.from(values ?? [], (value) => String(value ?? '')));
    wanted.delete('');

    const enabled = this.optionTargets.filter((opt) => this.#isEnabled(opt));
    const listed = new Set(enabled.map((opt) => opt.dataset.value ?? ''));
    if ([...wanted].some((value) => !listed.has(value))) return false;
    const exclusiveWanted = enabled.some(
      (opt) => this.#isExclusive(opt) && wanted.has(opt.dataset.value ?? '')
    );
    if (exclusiveWanted && wanted.size > 1) return false;

    enabled.forEach((opt) => this.#setChecked(opt, wanted.has(opt.dataset.value ?? '')));
    this.#paintMultiple();
    if (!silent) this.#dispatchChange();
    return true;
  }

  // MULTIPLE MODE, public locker (see the header, D2): locks (`{ reason, explanation }`) or unlocks
  // (`null`) the rows named by the keys of `map`, tick untouched, all or nothing. Returns whether it wrote.
  setDisabled(map, { silent = false } = {}) {
    this.#requireMode(true, 'setDisabled');
    const entries = Object.entries(map ?? {});
    const byValue = new Map(this.optionTargets.map((opt) => [opt.dataset.value ?? '', opt]));
    if (entries.some(([value]) => !byValue.has(value))) return false;

    const before = JSON.stringify(this.values);
    entries.forEach(([value, lock]) => this.#lockOption(byValue.get(value), lock));
    this.#paintMultiple();
    if (!silent && JSON.stringify(this.values) !== before) this.#dispatchChange();
    return true;
  }

  // PROGRAMMATIC SELECTION, for another controller (never an action): chooses the listed option whose
  // `data-value` is `value` through the SAME path as a click (hidden input, trigger, ticks, bubbling
  // `change`), without taking focus away from where the user is. The blank row answers ''. Returns
  // whether such an option exists; an unknown value writes nothing and dispatches nothing. A disabled
  // row is refused, as `select()` refuses it. Caller: `remark-lot` (O12, the freed « Concerné »).
  // SINGLE MODE ONLY: throws in multiple mode (`setValues`).
  selectValue(value) {
    this.#requireMode(false, 'selectValue');
    const option = this.#choosableOption(value);
    if (!option) return false;

    this.#applySelection(this.#optionSelection(option), { refocus: false });
    return true;
  }

  // SILENT WRITE, for another controller that MIRRORS a state it owns (never an action): paints the
  // option of that value exactly as a choice does (hidden input, trigger label + title + dot,
  // aria-selected, ticks) but dispatches NO `change`, leaves focus where it is and leaves the panel
  // as it is. Caller: `remarks-filter`, which resyncs its lists after every stream that re-renders
  // the §7. Through `selectValue` its own `change` would bubble back to `remarks-filter#filterStatus`
  // → `#apply` → resync → `selectValue`… : an infinite loop (plan review C2). Same answer and same
  // refusals as `selectValue`: false (nothing written) for an unknown value or a disabled row.
  //
  // CLEARING: '' with NO option keyed on '' at all (the CR remark « Statut » of a soldée remark, or
  // of a status the agency retired, renders no selection and no blank row) clears the selection
  // back to what the server renders for an empty value: hidden input '', the muted placeholder in
  // the trigger (and its title), no dot, no aria-selected, no tick. `cr-inline-field` repaints a
  // failed save's restored '' this way; refusing left the never-saved status on screen. A list that
  // HAS a '' option (the blank row, a « … (retiré) » option) keeps painting that option. Never
  // gated by the save lock (`#locked`): the restore runs while the hidden input is still disabled.
  // SINGLE MODE ONLY: throws in multiple mode (`setValues(values, { silent: true })`).
  writeValueSilently(value) {
    this.#requireMode(false, 'writeValueSilently');
    const option = this.#choosableOption(value);
    if (option) {
      this.#paintSelection(this.#optionSelection(option));
      return true;
    }

    if (String(value ?? '') !== '' || this.optionTargets.some((opt) => this.#isBlankValued(opt))) {
      return false;
    }

    this.#paintSelection({
      value: '',
      label: '',
      dotClass: undefined,
      isBlank: true,
      cleared: true,
    });
    return true;
  }

  // Is this option keyed on the empty value (the blank row, or a real '' option)?
  #isBlankValued(option) {
    return (option.dataset.value ?? '') === '';
  }

  // A public writer/reader called in the wrong mode fails loudly (header, PUBLIC API IN MULTIPLE MODE).
  #requireMode(multiple, name) {
    if (this.#multiple === multiple) return;
    const [wanted, actual, instead] = multiple
      ? ['multiple', 'single', 'selectValue / writeValueSilently']
      : ['single', 'multiple', 'setValues / values / setDisabled'];
    throw new Error(
      `searchable-select: ${name} is for ${wanted} mode only; this select is ${actual}, use ${instead}`
    );
  }

  // ── MULTIPLE MODE ──────────────────────────────────────────────────────────────────────────────────

  // One row of `setDisabled`: the attribute, the action, the two texts. No class: the component styles
  // the locked look from `aria-disabled` alone.
  #lockOption(option, lock) {
    const locked = lock !== null && lock !== undefined;
    if (locked) {
      option.setAttribute('aria-disabled', 'true');
    } else {
      option.removeAttribute('aria-disabled');
    }
    const actions = (option.getAttribute('data-action') ?? '')
      .split(/\s+/)
      .filter((token) => token !== '' && token !== SELECT_ACTION);
    if (!locked) actions.push(SELECT_ACTION);
    if (actions.length > 0) {
      option.setAttribute('data-action', actions.join(' '));
    } else {
      option.removeAttribute('data-action');
    }
    this.#writeRole(option, REASON_TEXT_ROLE, locked ? lock.reason : '');
    this.#writeRole(option, EXPLANATION_ROLE, locked ? lock.explanation : '');
  }

  #writeRole(option, role, text) {
    const node = option.querySelector(`[data-searchable-select-role="${role}"]`);
    if (node) node.textContent = String(text ?? '');
  }

  #isChecked(option) {
    return option.getAttribute?.('aria-selected') === 'true';
  }

  #isEnabled(option) {
    return option.getAttribute?.('aria-disabled') !== 'true';
  }

  #isExclusive(option) {
    return option.dataset.exclusive === 'true';
  }

  // aria-selected + the tick (`hidden` on the checkmark WRAPPER, its `flex` lives one level down).
  #setChecked(option, checked) {
    option.setAttribute('aria-selected', String(checked));
    const checkmark = option.querySelector('[data-searchable-select-target="checkmark"]');
    if (checkmark) checkmark.classList.toggle('hidden', !checked);
  }

  // One click / Enter on an ENABLED row (`select` already refused a disabled one and the save lock).
  // The exclusive rule only ever unticks ENABLED rows, then ONE repaint and ONE `change`.
  #toggleOption(option) {
    const checked = !this.#isChecked(option);
    this.#setChecked(option, checked);
    if (checked) {
      const exclusive = this.#isExclusive(option);
      this.optionTargets.forEach((other) => {
        if (other === option || !this.#isEnabled(other)) return;
        if (exclusive || this.#isExclusive(other)) this.#setChecked(other, false);
      });
    }
    this.#paintMultiple();
    this.#dispatchChange();
  }

  // Everything a multiple-mode write repaints, from the rows' aria-selected: the posted inputs, the
  // counter and the trigger. Text only (textContent / setAttribute), inputs by createElement (#591).
  #paintMultiple() {
    const checked = this.optionTargets.filter((opt) => this.#isChecked(opt));
    const posted = checked.filter((opt) => this.#isEnabled(opt));

    const name = this.hiddenInputTarget.getAttribute('name') ?? '';
    this.valuesTarget.replaceChildren(
      ...posted.map((opt) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        input.setAttribute('name', name);
        input.setAttribute('value', opt.dataset.value ?? '');
        input.setAttribute('data-searchable-select-target', 'valueInput');
        return input;
      })
    );

    // D1: every TICKED row, disabled (derived) ones included, as the server renders it. A ticked
    // exclusive row (« Toutes les entreprises ») is not a lot: it names itself (QA 01/11).
    const exclusive = checked.find((opt) => this.#isExclusive(opt));
    const count = checked.length;
    this.counterTarget.textContent = exclusive
      ? this.#optionText(exclusive)
      : `${count} ${count <= 1 ? this.counterOneValue : this.counterOtherValue}`;

    // External trigger: no trigger, nothing to repaint there.
    if (!this.hasSelectedTarget) return;
    // Disabled rows INCLUDED: the trigger shows what is ticked, as the server renders it.
    const labels = checked.map((opt) => this.#optionText(opt));
    const text =
      labels.length > 0 ? labels.join(MULTIPLE_TRIGGER_SEPARATOR) : this.placeholderValue || '';
    this.selectedTarget.textContent = text;
    this.selectedTarget.classList.toggle('text-neutral-900', labels.length > 0);
    this.selectedTarget.classList.toggle('text-neutral-500', labels.length === 0);
    this.selectedTarget.setAttribute('title', text);
  }

  // The component's ONE value signal, from the leading hidden input (multiple mode).
  #dispatchChange() {
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // The listed, enabled option whose `data-value` is `value` ('' = the blank row), or undefined.
  // Only `option` targets: a list separator (`<li role="presentation">`, no target, no data-value)
  // can never answer, exactly as it is never walked by the keyboard.
  #choosableOption(value) {
    const wanted = String(value ?? '');
    const option = this.optionTargets.find((opt) => (opt.dataset.value ?? '') === wanted);
    if (!option || option.getAttribute?.('aria-disabled') === 'true') return undefined;
    return option;
  }

  // What choosing a listed <li> writes: its value, label, dot and blank marker, plus, in
  // tree mode, its parents' labels from the root down.
  #optionSelection(option) {
    const ancestors = this.#isTreeMode() ? this.#ancestorsOf(option).reverse() : [];
    return {
      value: option.dataset.value ?? '',
      label: this.#optionText(option),
      dotClass: option.dataset.dotClass,
      isBlank: option.dataset.blank === 'true',
      path: ancestors.map((ancestor) => this.#optionText(ancestor)),
    };
  }

  // The one place a CHOICE lands: the paint (`#paintSelection`), then close + refocus + `change`.
  // `refocus: false` is for a programmatic choice (`selectValue`): the user's focus is wherever
  // they put it, pulling it back to the trigger would steal it.
  #applySelection(selection, { refocus = true } = {}) {
    this.#paintSelection(selection);

    this.close();
    if (refocus) this.triggerTarget.focus();
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // The one place a value is WRITTEN: hidden input, trigger text + title + dot, aria-selected and
  // ticks. No event, no focus, no panel: shared by a choice (`#applySelection`) and the silent
  // write (`writeValueSilently`), so the two can never paint differently.
  #paintSelection({ value, label, dotClass, isBlank, path = [], cleared = false }) {
    this.hiddenInputTarget.value = value;

    // `isBlank` is the `data-blank` MARKER the template puts on the `include_blank` row
    // and on it alone — never the empty VALUE. A real option may be keyed on '': the CR
    // remark form offers the withdrawn status an orphaned remark still carries
    // (« … (retiré) »), whose '' means « statut inchangé » to the controller. Reading the
    // value instead repainted the trigger « Choisir un statut » while the dot kept its
    // colour and the row its tick — the screen lying about the state.

    // A blank/cleared choice shows the muted placeholder; a real choice shows its label.
    // `blankIsChoice`: the blank row IS a choice (« Aucun (racine) »), shown like any other.
    // `cleared` (a silent '' with no '' option) has no row to show: always the placeholder.
    if (cleared || (isBlank && !this.blankIsChoiceValue)) {
      this.selectedTarget.textContent = this.placeholderValue || '';
      this.selectedTarget.classList.add('text-neutral-500');
      this.selectedTarget.classList.remove('text-neutral-900');
    } else {
      this.selectedTarget.textContent = label;
      this.selectedTarget.classList.remove('text-neutral-500');
      this.selectedTarget.classList.add('text-neutral-900');
    }
    if (this.hasSelectedPathTarget) {
      // TREE MODE (D12): parents above, lot below, the full path in `title`. Text only.
      this.selectedPathTarget.textContent = path.join(PATH_SEPARATOR);
      this.selectedTarget.setAttribute(
        'title',
        [...path, this.selectedTarget.textContent].join(PATH_SEPARATOR)
      );
    } else if (this.selectedTarget.hasAttribute('title')) {
      this.selectedTarget.setAttribute('title', this.selectedTarget.textContent);
    }

    // AFTER the label, and on BOTH branches: a blank choice carries no dot_class, so it
    // hides the dot just like a plain option list does.
    if (this.hasDotTarget) this.#syncDot(dotClass);

    this.optionTargets.forEach((opt) => {
      const isSelected = (opt.dataset.value ?? '') === value;
      opt.setAttribute('aria-selected', String(isSelected));
      const checkmark = opt.querySelector('[data-searchable-select-target="checkmark"]');
      if (checkmark) checkmark.classList.toggle('hidden', !isSelected);
    });
  }

  handleTriggerKeydown(event) {
    if (['ArrowDown', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (!this.isOpen) {
        this.open();
        return;
      }
    }

    // CLOSED LIST mode: focus never leaves the trigger, so the trigger is ALSO the
    // keyboard host of the listbox. Route the navigation keys to the one handler that
    // owns the roving highlight instead of duplicating it. In searchable mode focus has
    // moved into the input, which receives these keydowns itself.
    if (!this.hasInputTarget && this.isOpen) this.handleKeydown(event);
  }

  // Keyboard host of the listbox: the search input in searchable mode, the trigger in
  // closed-list mode (routed here by handleTriggerKeydown). Every inputTarget read behind
  // it is guarded by hasInputTarget (updateFocusedOption).
  handleKeydown(event) {
    const visibleOptions = this.#navigableOptions();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedIndex = Math.min(this.focusedIndex + 1, visibleOptions.length - 1);
        this.updateFocusedOption(visibleOptions);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedIndex = Math.max(this.focusedIndex - 1, 0);
        this.updateFocusedOption(visibleOptions);
        break;
      case 'Enter':
        event.preventDefault();
        this.#chooseFocused(visibleOptions);
        break;
      // Space is MODE-DEPENDENT, because the keyboard host is: in closed-list mode it is
      // the <button> trigger, where Space is no character and would merely re-toggle the
      // panel — so it mirrors Enter on the highlighted row (WAI-ARIA listbox pattern). In
      // searchable mode this handler is bound to the SEARCH FIELD itself, where Space IS a
      // character: cancelling it types « sarlb » for « sarl b ». Bail out, never preventDefault.
      case ' ':
        if (this.hasInputTarget) break;

        event.preventDefault();
        this.#chooseFocused(visibleOptions);
        break;
      // Tab moves focus on: the panel closes but the key is NEVER cancelled, so the
      // user is not trapped inside the field. MULTIPLE MODE: the panel stays open so focus
      // reaches « Terminer »; it closes when focus leaves the component (`handleFocusOut`).
      case 'Tab':
        if (!this.#multiple) this.close();
        break;
      // Escape is handled by the document-level capture guard (escapeGuard) while
      // the panel is open — it stops propagation before the target phase, so a
      // `case 'Escape'` here could never fire.
      default:
        break;
    }
  }

  updateFocusedOption(visibleOptions) {
    const options = visibleOptions || this.optionTargets;
    options.forEach((opt, index) => {
      if (index === this.focusedIndex) {
        opt.classList.add('bg-primary-50');
        opt.scrollIntoView({ block: 'nearest' });
        this.#activeDescendantHost()?.setAttribute('aria-activedescendant', opt.id || '');
      } else {
        opt.classList.remove('bg-primary-50');
      }
    });
  }

  // A click on the external OPENER is not a click outside: the very click that opened the panel
  // bubbles to `document` after `open()` and would close it at once.
  clickOutside(event) {
    if (!this.isOpen || this.element.contains(event.target)) return;
    if (this.#external && this.opener?.contains?.(event.target)) return;
    this.close();
  }

  // Activates the roving-highlighted row, if any. `click()` is what the option's own
  // `data-action` is wired to, so the keyboard path goes through the exact same `select()`.
  #chooseFocused(visibleOptions) {
    if (this.focusedIndex >= 0 && visibleOptions[this.focusedIndex]) {
      visibleOptions[this.focusedIndex].click();
    }
  }

  // The rows the keyboard walks: the visible options.
  #navigableOptions() {
    return this.optionTargets.filter((opt) => !opt.classList.contains('hidden'));
  }

  // The option's `optionLabel` span, or null.
  #labelElement(option) {
    return option.querySelector('[data-searchable-select-target="optionLabel"]');
  }

  // The option's visible label (the `optionLabel` span), trimmed. textContent, so a
  // highlighted label still reads as its plain text, never as the <mark> markup.
  #optionText(option) {
    return (this.#labelElement(option)?.textContent ?? option.textContent ?? '').trim();
  }

  // Tree mode is DEDUCED from the markup: the trigger's path line, or a row with a parent.
  #isTreeMode() {
    return (
      Boolean(this.hasSelectedPathTarget) ||
      this.optionTargets.some((opt) => (opt.dataset.parentValue ?? '') !== '')
    );
  }

  // TREE MODE: value → option, the lookup `#ancestorsOf` walks. Built ONCE per caller and
  // passed in: `optionTargets` is a DOM query in Stimulus, and rebuilding the table for every
  // found row made each keystroke O(n²) (branch review, defect #6). Never cached across
  // calls: a Turbo morph may swap the options between two keystrokes.
  #optionsByValue(options = this.optionTargets) {
    return new Map(options.map((opt) => [opt.dataset.value ?? '', opt]));
  }

  // TREE MODE: the option's ancestors, nearest first, through `data-parent-value`. An empty
  // parent value is a root (it must never resolve to the blank row, whose value is ''), and
  // a cycle in the markup stops the walk instead of looping.
  #ancestorsOf(option, byValue = this.#optionsByValue()) {
    const chain = [];
    const seen = new Set([option]);
    let parentValue = option.dataset.parentValue ?? '';
    while (parentValue !== '') {
      const parent = byValue.get(parentValue);
      if (!parent || seen.has(parent)) break;
      chain.push(parent);
      seen.add(parent);
      parentValue = parent.dataset.parentValue ?? '';
    }
    return chain;
  }

  // TREE MODE filter (D11): a found row, every one of its ancestors and the BLANK row stay
  // visible, and each found label gets its fragment highlighted. A pinned row (« Toutes les
  // entreprises ») is filtered like any other (QA-RL-33, planche 1f): `data-pinned` places it
  // outside the tree, it does not keep it on screen. Returns the number of RESULTS: the visible rows
  // minus the blank row, which stays whatever is typed and so answers nothing. Counted, it kept
  // « Aucun résultat » hidden under a lone « Aucun lot » on a search that found nothing (QA-RL-88).
  #filterTree(query) {
    const options = this.optionTargets;
    const byValue = this.#optionsByValue(options);
    const shown = new Set();
    const found = new Set();
    const ancestors = new Set();
    options.forEach((option) => {
      const label = this.#labelElement(option);
      this.#clearHighlight(label);
      if (query === '' || option.dataset.blank === 'true') shown.add(option);
      if (query === '') return;

      // The LABEL only (the intervenant sits outside it), untrimmed so the offsets are
      // those of the label's own text.
      const range = foldedRange(label ? label.textContent : this.#optionText(option), query);
      if (!range) return;

      shown.add(option);
      found.add(option);
      this.#ancestorsOf(option, byValue).forEach((ancestor) => {
        shown.add(ancestor);
        ancestors.add(ancestor);
      });
      if (label) this.#highlight(label, range);
    });
    // CONTEXT (QA-RL-177, planche 1f): an ancestor that does not match itself. Decided once every
    // row is known, because a parent comes BEFORE the descendant that keeps it. Empty search: none.
    options.forEach((option) => {
      option.classList.toggle('hidden', !shown.has(option));
      option.toggleAttribute(CONTEXT_ATTRIBUTE, ancestors.has(option) && !found.has(option));
    });
    this.#syncTreeHeading([...shown].some((option) => this.#isTreeRow(option)));
    return [...shown].filter((option) => option.dataset.blank !== 'true').length;
  }

  // TREE MODE: a row of the planning tree, i.e. neither the blank row nor a pinned row (both are
  // rendered ABOVE the « Lots du planning » heading, outside the tree).
  #isTreeRow(option) {
    return option.dataset.blank !== 'true' && option.dataset.pinned !== 'true';
  }

  // TREE MODE: the group heading shows only while at least one tree row does — a search that finds
  // no lot must not leave « LOTS DU PLANNING » alone (QA pass 1). Optional target, guarded.
  #syncTreeHeading(visible) {
    if (!this.hasTreeHeadingTarget) return;
    this.treeHeadingTarget.classList.toggle('hidden', !visible);
  }

  // Wraps [start, end) of the label in a <mark>, from DOM nodes only (#591): the text is
  // split into text nodes, never re-parsed as HTML.
  #highlight(label, [start, end]) {
    const text = label.textContent;
    const mark = document.createElement('mark');
    mark.setAttribute(HIGHLIGHT_ATTRIBUTE, '');
    if (this.hasHighlightClass) mark.classList.add(...this.highlightClasses);
    mark.textContent = text.slice(start, end);
    const nodes = [text.slice(0, start), mark, text.slice(end)]
      .filter((node) => node !== '')
      .map((node) => (typeof node === 'string' ? document.createTextNode(node) : node));
    label.replaceChildren(...nodes);
  }

  // Flattens a label this controller highlighted back to ONE text node of the same text.
  #clearHighlight(label) {
    if (!label?.querySelector(`mark[${HIGHLIGHT_ATTRIBUTE}]`)) return;
    const text = label.textContent;
    label.textContent = text;
  }

  // Repaints the trigger's dot from the colour class the SERVER put on the chosen <li>.
  // className is REBUILT from a literal so no stale colour survives a switch, and it
  // touches classes only — the target's attributes (data-…-target, aria-hidden) stay.
  #syncDot(dotClass) {
    this.dotTarget.className = dotClass ? `${DOT_BASE_CLASSES} ${dotClass}` : DOT_HIDDEN_CLASSES;
  }
}
